import React, { useEffect, useState } from 'react';
import { ActivityLog } from '@/models/LearnTypes';
import { activityService } from '@/services/ActivityService';

interface LevelDonutProps {
  lessonId: string;
  userId: string;
}

// Shape of our processed level data
interface LevelStatus {
  levelIndex: number;
  completed: boolean;
  isPerfect: boolean;
}

// Star SVG path for perfect lesson indicator
const starPath = "M12 1.5l2.7 8.3h8.8l-7.1 5.2 2.7 8.3-7.1-5.2-7.1 5.2 2.7-8.3-7.1-5.2h8.8z";

const LevelDonut: React.FC<LevelDonutProps> = ({ lessonId, userId }) => {
  console.debug('LevelDonut component rendering for:', { lessonId, userId });
  
  const [levelStatuses, setLevelStatuses] = useState<LevelStatus[]>([
    { levelIndex: 0, completed: false, isPerfect: false },
    { levelIndex: 1, completed: false, isPerfect: false },
    { levelIndex: 2, completed: false, isPerfect: false },
    { levelIndex: 3, completed: false, isPerfect: false },
  ]);
  const [loading, setLoading] = useState(true);
  const [isPerfectLesson, setIsPerfectLesson] = useState(false);

  useEffect(() => {
    const fetchLevelStatuses = async () => {
      if (!userId || !lessonId) return;
      
      try {
        setLoading(true);
        
        // Fetch all activities for this user
        const activities = await activityService.getUserActivities(userId);
        
        // Check if there's a perfect lesson completion record
        // First, log all lesson completions for this lesson to debug
        console.debug('All lesson activities:', activities.filter(a => a.type === 'lesson-completed' && a.lessonId === lessonId));
        
        // Log ALL activities for debugging
        console.debug(`Total activities found: ${activities.length}`);
        
        // Look for a perfect lesson completion - any lesson completion with isPerfectLesson set to true
        // First, find all lesson completions for this specific lesson
        const lessonCompletions = activities.filter(activity => 
          activity.type === 'lesson-completed' && 
          activity.lessonId === lessonId
        );
        
        console.debug(`Found ${lessonCompletions.length} completions for lesson ${lessonId}:`, lessonCompletions);
        
        // Check each one if it has isPerfectLesson=true
        const perfectLessonActivity = lessonCompletions.find(activity => activity.isPerfectLesson === true);
        
        // Set perfect lesson status based on activity log
        const hasPerfectLesson = !!perfectLessonActivity;
        console.debug(`Setting isPerfectLesson to: ${hasPerfectLesson}`);
        setIsPerfectLesson(hasPerfectLesson);
        
        // Debug perfect lesson status with more details
        console.debug(`Perfect lesson flag set: ${!!perfectLessonActivity}, lessonId: ${lessonId}`);
        if (perfectLessonActivity) {
          console.debug('Perfect lesson activity details:', perfectLessonActivity);
        } else {
          // If perfect lesson wasn't found, log all lesson completions to debug
          const lessonCompletions = activities.filter(a => a.type === 'lesson-completed');
          console.debug(`Found ${lessonCompletions.length} lesson completions in total`);
          lessonCompletions.forEach((activity, index) => {
            console.debug(`Lesson completion ${index + 1}:`, {
              lessonId: activity.lessonId,
              isPerfectLesson: activity.isPerfectLesson,
              timestamp: activity.timestamp
            });
          });
        }
        
        // Filter to find level completions for this specific lesson
        const levelCompletions = activities.filter(
          (activity) => 
            activity.type === 'level-completed' && 
            activity.lessonId === lessonId &&
            typeof activity.levelIndex === 'number'
        );
        
        // Debug the activity logs to help diagnose issues
        console.debug(`Found ${levelCompletions.length} level completions for lesson ${lessonId}`);
        levelCompletions.forEach(activity => {
          console.debug(`Level ${activity.levelIndex} completion: isPerfect=${activity.isPerfect}, XP=${activity.xpEarned}`);
        });
        
        // Process the activities to find ALL level completions
        // We need to track if a level was EVER completed perfectly
        const statusMap = new Map<number, LevelStatus>();
        
        // First pass: Mark all completed levels
        levelCompletions.forEach((activity) => {
          const levelIndex = activity.levelIndex!;
          
          // Initialize status if not already present
          if (!statusMap.has(levelIndex)) {
            statusMap.set(levelIndex, {
              levelIndex,
              completed: true,
              isPerfect: false // Default to not perfect
            });
          }
        });
        
        // Second pass: Check for ANY perfect completion for each level
        levelCompletions.forEach((activity) => {
          const levelIndex = activity.levelIndex!;
          // Cast to boolean to handle undefined/null cases
          const isPerfect = Boolean(activity.isPerfect);
          
          if (isPerfect) {
            // If we find ANY perfect completion, mark the level as perfect
            const status = statusMap.get(levelIndex);
            if (status) {
              statusMap.set(levelIndex, {
                ...status,
                isPerfect: true
              });
            }
          }
        });
        
        // Create a new array with updated statuses
        const newStatuses = [...levelStatuses];
        statusMap.forEach((status) => {
          if (status.levelIndex >= 0 && status.levelIndex < 4) {
            newStatuses[status.levelIndex] = status;
          }
        });
        
        // Debug log the final statuses
        console.debug('Final level statuses:', newStatuses.map(s => 
          `Level ${s.levelIndex}: completed=${s.completed}, perfect=${s.isPerfect}`
        ).join(', '));
        
        setLevelStatuses(newStatuses);
      } catch (error) {
        console.error('Error fetching level statuses:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLevelStatuses();
  }, [userId, lessonId]);
  
  // Calculate completed and perfect count for accessibility label
  const completedCount = levelStatuses.filter(status => status.completed).length;
  const perfectCount = levelStatuses.filter(status => status.isPerfect).length;
  
  if (loading) {
    return <div className="w-12 h-12 animate-pulse bg-gray-200 rounded-full"></div>;
  }
  
  return (
    <div 
      className="relative w-12 h-12" 
      aria-label={`${completedCount} of 4 levels completed, ${perfectCount} perfectly${isPerfectLesson ? ', perfect lesson completion' : ''}`}
    >
      {/* Donut chart */}
      <svg viewBox="0 0 48 48" className="w-full h-full">
        {/* Background circle (gray) */}
        <circle 
          cx="24" 
          cy="24" 
          r="20" 
          fill="#e2e8f0"
        />
        
        {/* Level segments */}
        {levelStatuses.map((status, index) => {
          // Calculate start angle for each segment (offset by -90° to start at the top)
          const startAngle = index * 90 - 90;
          // Calculate x and y coordinates for the path using the outer radius
          const x1 = 24 + 16 * Math.cos((startAngle * Math.PI) / 180);
          const y1 = 24 + 16 * Math.sin((startAngle * Math.PI) / 180);
          const x2 = 24 + 16 * Math.cos(((startAngle + 90) * Math.PI) / 180);
          const y2 = 24 + 16 * Math.sin(((startAngle + 90) * Math.PI) / 180);
          
          // Thicker circle segments with more spacing between them
          const outerRadius = 20; // Outer radius matches gray background
          // Recalculate the outer points with the updated radius
          const ox1 = 24 + outerRadius * Math.cos((startAngle * Math.PI) / 180);
          const oy1 = 24 + outerRadius * Math.sin((startAngle * Math.PI) / 180);
          const ox2 = 24 + outerRadius * Math.cos(((startAngle + 90) * Math.PI) / 180);
          const oy2 = 24 + outerRadius * Math.sin(((startAngle + 90) * Math.PI) / 180);
          
          // Smaller inner radius to make segments thicker
          const innerRadius = 6;
          const ix1 = 24 + innerRadius * Math.cos((startAngle * Math.PI) / 180);
          const iy1 = 24 + innerRadius * Math.sin((startAngle * Math.PI) / 180);
          const ix2 = 24 + innerRadius * Math.cos(((startAngle + 90) * Math.PI) / 180);
          const iy2 = 24 + innerRadius * Math.sin(((startAngle + 90) * Math.PI) / 180);
          
          // If not completed, don't render this segment
          if (!status.completed) return null;
          
          // Color based on perfection
          const color = status.isPerfect ? "#fbbf24" : "#22c55e";
          
          // Create a custom path with both outer and inner arcs for a thicker segment
          return (
            <path
              key={index}
              d={`M ${ox1} ${oy1} A ${outerRadius} ${outerRadius} 0 0 1 ${ox2} ${oy2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 0 0 ${ix1} ${iy1} Z`}
              fill={color}
              strokeLinejoin="round"
              stroke="none"
            />
          );
        })}
        
        {/* Inner white circle drawn last to ensure it's on top - smaller for thicker segments */}
        <circle
          cx="24"
          cy="24"
          r="6"
          fill="white"
        />
      </svg>
      
      {/* Crown for perfect lesson - sitting on the golden indicators */}
      {isPerfectLesson && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 -translate-y-1/4 z-10">
          <span 
            className="text-4xl" 
            style={{ 
              filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.4)) drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
              textShadow: '0 3px 5px rgba(0,0,0,0.3)'
            }}>
            👑
          </span>
        </div>
      )}
      

    </div>
  );
};

export default LevelDonut;
